import { forwardRef, ReactNode, useCallback, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useIntersectionObserver } from './useIntersectionObserver';

type InfiniteScrollProps = {
  children: ReactNode;
  onLoadMore: () => Promise<void>;
  hasMore?: boolean;
  isLoading?: boolean;
  threshold?: number;
  rootMargin?: string;
  root?: HTMLElement | null;
  loader?: ReactNode;
  className?: string;
  triggerClassName?: string;
  loaderClassName?: string;
};

const InfiniteScroll = forwardRef<HTMLDivElement, InfiniteScrollProps>(
  (
    {
      children,
      onLoadMore,
      hasMore = true,
      isLoading = false,
      threshold = 0.1,
      rootMargin = '0px',
      root,
      loader,
      className,
      triggerClassName,
      loaderClassName,
    },
    ref
  ) => {
    const [isLoadingInternal, setIsLoadingInternal] = useState(false);
    const isLoadingRef = useRef(false);

    const handleLoadMore = useCallback(async () => {
      if (isLoadingRef.current || isLoading || !hasMore) return;
      isLoadingRef.current = true;
      setIsLoadingInternal(true);
      try {
        await onLoadMore();
      } finally {
        isLoadingRef.current = false;
        setIsLoadingInternal(false);
      }
    }, [onLoadMore, isLoading, hasMore]);

    const { setElementRef } = useIntersectionObserver(
      handleLoadMore,
      root,
      rootMargin,
      threshold
    );

    const showLoader = (isLoading || isLoadingInternal) && hasMore;

    return (
      <div ref={ref} className={cn('relative', className)}>
        {children}

        {hasMore && (
          <div
            ref={setElementRef}
            className={cn('w-full h-px', triggerClassName)}
            aria-hidden="true"
          />
        )}

        {showLoader && (
          <div
            className={cn(
              'flex items-center justify-center py-4',
              loaderClassName
            )}
          >
            {loader || (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

InfiniteScroll.displayName = 'InfiniteScroll';

export { InfiniteScroll };
export type { InfiniteScrollProps };
